import {
  Alert,
  type AlertProps,
  AssetWatchLogo,
  Button,
  Divider,
  Group,
  Headline,
  Icon,
  Input,
  Stack,
  Text,
} from "@assetwatch/ui";
import type { ChangeEvent, ComponentPropsWithoutRef } from "react";
import { useState } from "react";
import {
  DEFAULT_PASSWORD_REQUIREMENT_LABELS,
  type PasswordRequirementLabels,
  PasswordRequirements,
} from "./PasswordRequirements";

type PasswordValidation = {
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialCharacter: boolean;
  hasSufficientLength: boolean;
  matchesConfirm: boolean;
};

type StatusMessage = {
  tone: NonNullable<AlertProps["tone"]>;
  message: string;
};

type CognitoErrorLike = {
  code?: unknown;
  message?: unknown;
  name?: unknown;
  __type?: unknown;
};

type ConfirmPasswordResetVariables = {
  confirmationCode: string;
  newPassword: string;
  username: string;
};

type FormSubmitHandler = NonNullable<
  ComponentPropsWithoutRef<"form">["onSubmit"]
>;
type FormSubmitEvent = Parameters<FormSubmitHandler>[0];

export type ResetPasswordPageMessages = {
  assetWatchLabel: string;
  backToSignIn: string;
  confirmNewPasswordLabel: string;
  continueToSignIn: string;
  description: string;
  emailLabel: string;
  emailPlaceholder: string;
  emailSentMessage: string;
  emailSentTitle: string;
  expiredLinkDescription: string;
  expiredLinkTitle: string;
  forgotPasswordDescription: string;
  forgotPasswordTitle: string;
  hidePassword: string;
  loadingSendingCode: string;
  loadingUpdatingPassword: string;
  newPasswordLabel: string;
  passwordRequirementsIntro: string;
  passwordSavedMessage: string;
  passwordSavedTitle: string;
  passwordsDoNotMatch: string;
  reachOutToSupport: string;
  resetCodeLabel: string;
  saveChanges: string;
  sendNewLink: string;
  sendResetCode: string;
  showPassword: string;
  supportQuestion: string;
  title: string;
  errors: {
    codeMismatch: string;
    expiredCode: string;
    generic: string;
    invalidPassword: string;
    limitExceeded: string;
    passwordHistoryViolation: string;
    unverifiedContact: string;
    userNotFound: string;
  };
  passwordRequirements?: PasswordRequirementLabels;
};

export type ResetPasswordPageProps = {
  confirmPasswordReset: (
    variables: ConfirmPasswordResetVariables,
  ) => Promise<unknown>;
  initialCode?: string;
  initialEmail?: string;
  initialExpired?: boolean;
  isConfirming: boolean;
  isSendingCode: boolean;
  messages: ResetPasswordPageMessages;
  onNavigateToSignIn: () => void;
  sendPasswordResetCode: (username: string) => Promise<unknown>;
  supportHref?: string;
};

type SendResetCodeFormProps = {
  canSendCode: boolean;
  email: string;
  isSendingCode: boolean;
  messages: ResetPasswordPageMessages;
  onEmailChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: FormSubmitHandler;
};

type ConfirmResetPasswordFormProps = {
  canConfirm: boolean;
  confirmPassword: string;
  isConfirming: boolean;
  messages: ResetPasswordPageMessages;
  newPassword: string;
  newPasswordError: string | null;
  onConfirmPasswordChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onNewPasswordChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onResetCodeChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: FormSubmitHandler;
  passwordValidation: PasswordValidation;
  resetCode: string;
  showResetCodeInput: boolean;
};

function validateNewPassword(
  password: string,
  confirmPassword: string,
): PasswordValidation {
  return {
    hasUppercase: password.toLowerCase() !== password,
    hasLowercase: password.toUpperCase() !== password,
    hasNumber: /[0-9]/.test(password),
    hasSpecialCharacter: /[^a-zA-Z0-9]/.test(password),
    hasSufficientLength: password.length >= 10 && password.length <= 32,
    matchesConfirm: password === confirmPassword,
  };
}

function isPasswordValid(validation: PasswordValidation) {
  return (
    validation.hasUppercase &&
    validation.hasLowercase &&
    validation.hasNumber &&
    validation.hasSpecialCharacter &&
    validation.hasSufficientLength &&
    validation.matchesConfirm
  );
}

function isCognitoErrorLike(error: unknown): error is CognitoErrorLike {
  return typeof error === "object" && error !== null;
}

function getStringProperty(
  error: CognitoErrorLike,
  property: keyof CognitoErrorLike,
) {
  const value = error[property];
  return typeof value === "string" ? value : undefined;
}

function getCognitoErrorName(error: CognitoErrorLike) {
  return (
    getStringProperty(error, "name") ??
    getStringProperty(error, "code") ??
    getStringProperty(error, "__type")?.split("#").pop() ??
    ""
  );
}

function getErrorMessage(
  error: unknown,
  messages: ResetPasswordPageMessages,
) {
  if (!isCognitoErrorLike(error)) {
    return messages.errors.generic;
  }

  const name = getCognitoErrorName(error);
  const message = getStringProperty(error, "message") ?? "";

  if (name === "InvalidPasswordException") {
    return messages.errors.invalidPassword;
  }

  if (
    name === "InvalidParameterException" &&
    message.includes("no registered/verified email")
  ) {
    return messages.errors.unverifiedContact;
  }

  const cognitoErrorMessages: Partial<Record<string, string>> = {
    CodeMismatchException: messages.errors.codeMismatch,
    ExpiredCodeException: messages.errors.expiredCode,
    LimitExceededException: messages.errors.limitExceeded,
    TooManyRequestsException: messages.errors.limitExceeded,
    UserNotFoundException: messages.errors.userNotFound,
  };

  return cognitoErrorMessages[name] ?? (message || messages.errors.generic);
}

function isExpiredResetCodeError(error: unknown) {
  return (
    isCognitoErrorLike(error) &&
    getCognitoErrorName(error) === "ExpiredCodeException"
  );
}

function isCodeMismatchResetCodeError(error: unknown) {
  return (
    isCognitoErrorLike(error) &&
    getCognitoErrorName(error) === "CodeMismatchException"
  );
}

function isNewPasswordFieldError(error: unknown) {
  if (!isCognitoErrorLike(error)) {
    return false;
  }

  const name = getCognitoErrorName(error);

  return (
    name === "PasswordHistoryPolicyViolationException" ||
    name === "InvalidPasswordException"
  );
}

function getNewPasswordFieldErrorMessage(
  error: unknown,
  messages: ResetPasswordPageMessages,
) {
  if (!isCognitoErrorLike(error)) {
    return messages.errors.invalidPassword;
  }

  if (
    getCognitoErrorName(error) === "PasswordHistoryPolicyViolationException"
  ) {
    return messages.errors.passwordHistoryViolation;
  }

  return messages.errors.invalidPassword;
}

function SendResetCodeForm({
  canSendCode,
  email,
  isSendingCode,
  messages,
  onEmailChange,
  onSubmit,
}: SendResetCodeFormProps) {
  return (
    <form onSubmit={onSubmit}>
      <Stack gap={40}>
        <Input
          autoComplete="email"
          label={messages.emailLabel}
          onChange={onEmailChange}
          placeholder={messages.emailPlaceholder}
          type="email"
          value={email}
        />
        <Button
          disabled={!canSendCode}
          fullWidth
          loading={isSendingCode}
          loadingText={messages.loadingSendingCode}
          type="submit"
        >
          {messages.sendResetCode}
        </Button>
      </Stack>
    </form>
  );
}

function ConfirmResetPasswordForm({
  canConfirm,
  confirmPassword,
  isConfirming,
  messages,
  newPassword,
  newPasswordError,
  onConfirmPasswordChange,
  onNewPasswordChange,
  onResetCodeChange,
  onSubmit,
  passwordValidation,
  resetCode,
  showResetCodeInput,
}: ConfirmResetPasswordFormProps) {
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);

  return (
    <form onSubmit={onSubmit}>
      <Stack gap={40}>
        <Stack gap={16}>
          {showResetCodeInput ? (
            <Input
              autoComplete="one-time-code"
              label={messages.resetCodeLabel}
              onChange={onResetCodeChange}
              value={resetCode}
            />
          ) : null}
          <Input
            autoComplete="new-password"
            error={newPasswordError ?? undefined}
            label={messages.newPasswordLabel}
            onChange={onNewPasswordChange}
            trailingAction={{
              icon: isNewPasswordVisible ? "eye-watching" : "eye-slash-regular",
              "aria-label": isNewPasswordVisible
                ? messages.hidePassword
                : messages.showPassword,
              "aria-pressed": isNewPasswordVisible,
              onClick: () => setIsNewPasswordVisible((current) => !current),
            }}
            type={isNewPasswordVisible ? "text" : "password"}
            value={newPassword}
          />
          <PasswordRequirements
            labels={
              messages.passwordRequirements ??
              DEFAULT_PASSWORD_REQUIREMENT_LABELS
            }
            requirements={passwordValidation}
            showPreviousPasswordRequirement={false}
            title={messages.passwordRequirementsIntro}
            variant="panel"
          />
          <Input
            autoComplete="new-password"
            error={
              confirmPassword.length > 0 && !passwordValidation.matchesConfirm
                ? messages.passwordsDoNotMatch
                : undefined
            }
            label={messages.confirmNewPasswordLabel}
            onChange={onConfirmPasswordChange}
            trailingAction={{
              icon: isConfirmPasswordVisible
                ? "eye-watching"
                : "eye-slash-regular",
              "aria-label": isConfirmPasswordVisible
                ? messages.hidePassword
                : messages.showPassword,
              "aria-pressed": isConfirmPasswordVisible,
              onClick: () => setIsConfirmPasswordVisible((current) => !current),
            }}
            type={isConfirmPasswordVisible ? "text" : "password"}
            value={confirmPassword}
          />
        </Stack>
        <Button
          disabled={!canConfirm}
          fullWidth
          loading={isConfirming}
          loadingText={messages.loadingUpdatingPassword}
          type="submit"
        >
          {messages.saveChanges}
        </Button>
      </Stack>
    </form>
  );
}

export function ResetPasswordPage({
  confirmPasswordReset,
  initialCode = "",
  initialEmail = "",
  initialExpired = false,
  isConfirming,
  isSendingCode,
  messages,
  onNavigateToSignIn,
  sendPasswordResetCode,
  supportHref = "mailto:support@assetwatch.com",
}: ResetPasswordPageProps) {
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState(initialCode);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [hasSentEmail, setHasSentEmail] = useState(false);
  const [hasCollectedEmailForCode, setHasCollectedEmailForCode] =
    useState(Boolean(initialCode) && Boolean(initialEmail));
  const [hasEmailForExpiredLinkResend, setHasEmailForExpiredLinkResend] =
    useState(Boolean(initialExpired) && Boolean(initialEmail));
  const [isResetLinkFlow] = useState(
    Boolean(initialCode) && Boolean(initialEmail),
  );
  const [isLinkExpired, setIsLinkExpired] = useState(initialExpired);
  const [isResetCodeEditable, setIsResetCodeEditable] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [status, setStatus] = useState<StatusMessage | null>(null);

  const normalizedEmail = email.trim().toLowerCase();
  const hasResetCode = code.trim().length > 0;
  const canSendCode = normalizedEmail.length > 0 && !isSendingCode;
  const passwordValidation = validateNewPassword(newPassword, confirmPassword);
  const canConfirm =
    normalizedEmail.length > 0 &&
    hasResetCode &&
    isPasswordValid(passwordValidation) &&
    !isConfirming;

  async function handleSendCode(event?: FormSubmitEvent) {
    event?.preventDefault();

    if (!canSendCode) {
      return;
    }

    setStatus(null);

    try {
      if (needsEmailForExistingCode) {
        setHasCollectedEmailForCode(true);
        return;
      }

      if (needsEmailForExpiredLink) {
        setHasEmailForExpiredLinkResend(true);
        return;
      }

      await sendPasswordResetCode(normalizedEmail);
      setHasSentEmail(true);
      setIsLinkExpired(false);
      setIsResetCodeEditable(false);
      setHasEmailForExpiredLinkResend(false);
      setCode("");
      setStatus(null);
    } catch (error) {
      setStatus({
        tone: "negative",
        message: getErrorMessage(error, messages),
      });
    }
  }

  async function handleConfirmReset(event: FormSubmitEvent) {
    event.preventDefault();

    if (!canConfirm) {
      return;
    }

    setStatus(null);
    setNewPasswordError(null);

    try {
      await confirmPasswordReset({
        username: normalizedEmail,
        confirmationCode: code.trim(),
        newPassword,
      });
      setIsComplete(true);
      setStatus(null);
    } catch (error) {
      if (isExpiredResetCodeError(error)) {
        setIsLinkExpired(true);
        setHasEmailForExpiredLinkResend(normalizedEmail.length > 0);
        setStatus(null);
      } else if (isCodeMismatchResetCodeError(error)) {
        setIsResetCodeEditable(true);
        setStatus({
          tone: "negative",
          message: getErrorMessage(error, messages),
        });
      } else if (isNewPasswordFieldError(error)) {
        setNewPasswordError(getNewPasswordFieldErrorMessage(error, messages));
        setStatus(null);
      } else {
        setStatus({
          tone: "negative",
          message: getErrorMessage(error, messages),
        });
      }
    }
  }

  const isEmailSentStep = !isComplete && hasSentEmail && !isLinkExpired;
  const isExpiredLinkStep = !isComplete && isLinkExpired;
  const needsEmailForExistingCode =
    !isComplete &&
    !hasSentEmail &&
    hasResetCode &&
    !hasCollectedEmailForCode &&
    !isLinkExpired;
  const needsEmailForExpiredLink =
    isExpiredLinkStep &&
    !hasEmailForExpiredLinkResend;
  const hasEmailForExistingCode =
    hasResetCode && (isResetLinkFlow || hasCollectedEmailForCode);
  const hasEmailForExpiredLink =
    isExpiredLinkStep && hasEmailForExpiredLinkResend;
  const isInitialForgotPasswordStep =
    !isComplete &&
    !hasSentEmail &&
    !isResetLinkFlow &&
    !hasResetCode &&
    !isLinkExpired;
  const shouldShowEmailForm =
    isInitialForgotPasswordStep ||
    needsEmailForExistingCode ||
    needsEmailForExpiredLink;
  const shouldShowResetCodeInput =
    hasSentEmail || !hasResetCode || isResetCodeEditable;
  const isCreatePasswordStep =
    !isComplete &&
    !isLinkExpired &&
    normalizedEmail.length > 0 &&
    (hasSentEmail || hasEmailForExistingCode);
  const headerTitle = isComplete
    ? messages.passwordSavedTitle
    : isEmailSentStep
      ? messages.emailSentTitle
      : isExpiredLinkStep
        ? messages.expiredLinkTitle
        : isInitialForgotPasswordStep
          ? messages.forgotPasswordTitle
          : messages.title;
  const showSupportFooter = isInitialForgotPasswordStep || isExpiredLinkStep;

  return (
    <main className="min-h-screen bg-surface-container-lowest">
      <div className="flex min-h-screen flex-col">
        <div className="flex min-h-[64px] items-center bg-surface-container-lowest px-(--spacing-16)">
          <AssetWatchLogo aria-label={messages.assetWatchLabel} />
        </div>
        <Divider aria-hidden variant="full-width" />
        <div className="flex flex-1 flex-col items-center justify-between px-(--spacing-20)">
          <div className="flex w-full justify-center pt-(--spacing-40) md:pt-(--spacing-80)">
            <div className="w-full max-w-[480px]">
              <Stack gap={40} fullWidth>
                <Stack align="center" gap={16} fullWidth>
                  <div className="[&_svg]:h-auto [&_svg]:w-[90px]">
                    <AssetWatchLogo aria-hidden tone="default" />
                  </div>
                  <Headline align="center" as="h1" size="sm">
                    {headerTitle}
                  </Headline>
                  <div className={isEmailSentStep ? "w-full" : "max-w-[334px]"}>
                    <Text align="center" tone="subtext">
                      {isComplete ? (
                        messages.passwordSavedMessage
                      ) : isEmailSentStep ? (
                        <>
                          {messages.emailSentMessage}
                          <br />
                          <span className="font-(number:--font-weight-semibold) text-text-default">
                            {normalizedEmail}
                          </span>
                        </>
                      ) : isExpiredLinkStep ? (
                        messages.expiredLinkDescription
                      ) : shouldShowEmailForm ? (
                        messages.forgotPasswordDescription
                      ) : (
                        messages.description
                      )}
                    </Text>
                  </div>
                </Stack>

                {status && !isComplete ? (
                  <Alert tone={status.tone} title={status.message} />
                ) : null}

                {isComplete ? (
                  <Button fullWidth type="button" onClick={onNavigateToSignIn}>
                    {messages.continueToSignIn}
                  </Button>
                ) : (
                  <Stack gap={24}>
                    {isExpiredLinkStep && !shouldShowEmailForm ? (
                      <>
                        <Button
                          disabled={!hasEmailForExpiredLink}
                          fullWidth
                          loading={isSendingCode}
                          loadingText={messages.loadingSendingCode}
                          type="button"
                          onClick={() => void handleSendCode()}
                        >
                          {messages.sendNewLink}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={onNavigateToSignIn}
                        >
                          {messages.backToSignIn}
                        </Button>
                      </>
                    ) : null}

                    {shouldShowEmailForm ? (
                      <SendResetCodeForm
                        canSendCode={canSendCode}
                        email={email}
                        isSendingCode={isSendingCode}
                        messages={messages}
                        onEmailChange={(event) =>
                          setEmail(event.currentTarget.value)
                        }
                        onSubmit={(event) => void handleSendCode(event)}
                      />
                    ) : null}

                    {isCreatePasswordStep ? (
                      <ConfirmResetPasswordForm
                        canConfirm={canConfirm}
                        confirmPassword={confirmPassword}
                        isConfirming={isConfirming}
                        messages={messages}
                        newPassword={newPassword}
                        newPasswordError={newPasswordError}
                        onConfirmPasswordChange={(event) =>
                          setConfirmPassword(event.currentTarget.value)
                        }
                        onNewPasswordChange={(event) => {
                          setNewPassword(event.currentTarget.value);
                          setNewPasswordError(null);
                        }}
                        onResetCodeChange={(event) =>
                          setCode(event.currentTarget.value)
                        }
                        onSubmit={(event) => void handleConfirmReset(event)}
                        passwordValidation={passwordValidation}
                        resetCode={code}
                        showResetCodeInput={shouldShowResetCodeInput}
                      />
                    ) : null}
                  </Stack>
                )}
              </Stack>
            </div>
          </div>
          {showSupportFooter ? (
            <div className="w-full py-(--spacing-40)">
              <Group gap={0} justify="center">
                <Text as="span" tone="subtext">
                  {messages.supportQuestion}
                </Text>
                <Button
                  render={
                    <a
                      aria-label={messages.reachOutToSupport}
                      href={supportHref}
                    >
                      {messages.reachOutToSupport}
                      <Icon
                        aria-hidden
                        name="arrow-up-right"
                        size="sm"
                        tone="inherit"
                      />
                    </a>
                  }
                  size="sm"
                  variant="ghost"
                >
                  {messages.reachOutToSupport}
                </Button>
              </Group>
            </div>
          ) : null}
        </div>
      </div>
    </main>
  );
}
